import React, { useState, useEffect } from 'react';
import { Search, User, DollarSign, Calendar, Edit2, Trash2, X, CreditCard } from 'lucide-react';
import financeService from '../../services/financeService';

const PaymentHistory = ({ onEdit }) => {
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [patientStats, setPatientStats] = useState(null);

  useEffect(() => {
    fetchPatientSummary();
  }, []);

  const fetchPatientSummary = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await financeService.getPatientSummary();
      setPatients(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load patients');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPatientPayments = async (patientId) => {
    try {
      setPaymentsLoading(true);
      const response = await financeService.getPatientPayments(patientId);
      setPayments(response.data.payments);
      setPatientStats({
        total_paid: response.data.total_paid,
        total_pending: response.data.total_pending,
        total_amount: response.data.total_amount
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load payment history');
      console.error(err);
    } finally {
      setPaymentsLoading(false);
    }
  };

  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
    fetchPatientPayments(patient.patient_id);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this payment?')) return;
    
    try {
      await financeService.deletePayment(id);
      fetchPatientPayments(selectedPatient.patient_id);
      fetchPatientSummary(); // Refresh patient list
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete payment');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      Paid: 'bg-green-100 text-green-800',
      Pending: 'bg-orange-100 text-orange-800',
      Partial: 'bg-blue-100 text-blue-800'
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  const filteredPatients = patients.filter(patient =>
    patient.patient_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    patient.phone.includes(searchTerm)
  );

  return (
    <div className="flex gap-4 h-[calc(100vh-12rem)]">
      {/* Left Panel - Patient List */}
      <div className="w-1/3 bg-white rounded-lg shadow-md border border-gray-200 flex flex-col">
        {/* Search Header */}
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by name or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div className="mt-2 text-sm text-gray-600">
            {filteredPatients.length} patient{filteredPatients.length !== 1 ? 's' : ''} found
          </div>
        </div>

        {/* Patient List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : error ? (
            <div className="p-4 text-center text-red-600">{error}</div>
          ) : filteredPatients.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              {searchTerm ? 'No patients found' : 'No payment records yet'}
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredPatients.map((patient) => (
                <div
                  key={patient.patient_id}
                  onClick={() => handlePatientSelect(patient)}
                  className={`p-4 cursor-pointer transition-colors ${
                    selectedPatient?.patient_id === patient.patient_id
                      ? 'bg-indigo-50 border-l-4 border-indigo-600'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 mt-1">
                        <User className="w-5 h-5 text-gray-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-semibold text-gray-900 truncate">
                          {patient.patient_name}
                        </h3>
                        <p className="text-xs text-gray-500 mt-1">{patient.phone}</p>
                        <div className="mt-2 flex flex-col space-y-1">
                          <div className="flex items-center text-xs">
                            <DollarSign className="w-3 h-3 mr-1 text-green-600" />
                            <span className="font-medium text-green-700">
                              {formatCurrency(patient.total_paid)}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500">
                            {patient.payment_count} transaction{patient.payment_count !== 1 ? 's' : ''}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Right Panel - Payment History */}
      <div className="flex-1 bg-white rounded-lg shadow-md border border-gray-200 flex flex-col">
        {!selectedPatient ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">Select a patient to view payment history</p>
            </div>
          </div>
        ) : (
          <>
            {/* Patient Header */}
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-white">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <h2 className="text-xl font-bold text-gray-900">
                      {selectedPatient.patient_name}
                    </h2>
                    <button
                      onClick={() => setSelectedPatient(null)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{selectedPatient.phone}</p>
                  
                  {patientStats && (
                    <div className="mt-4 grid grid-cols-3 gap-4">
                      <div className="bg-white rounded-lg p-3 border border-gray-200">
                        <div className="text-xs text-gray-500 mb-1">Total Paid</div>
                        <div className="text-lg font-bold text-green-600">
                          {formatCurrency(patientStats.total_paid)}
                        </div>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-gray-200">
                        <div className="text-xs text-gray-500 mb-1">Pending</div>
                        <div className="text-lg font-bold text-orange-600">
                          {formatCurrency(patientStats.total_pending)}
                        </div>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-gray-200">
                        <div className="text-xs text-gray-500 mb-1">Total</div>
                        <div className="text-lg font-bold text-indigo-600">
                          {formatCurrency(patientStats.total_amount)}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Payment History Table */}
            <div className="flex-1 overflow-auto">
              {paymentsLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                </div>
              ) : payments.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <CreditCard className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No payment records found</p>
                  </div>
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Mode
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Notes
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {payments.map((payment) => (
                      <tr key={payment.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-900">
                            <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                            {formatDate(payment.payment_date)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-semibold text-gray-900">
                            {formatCurrency(payment.amount)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{payment.payment_mode}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(payment.payment_status)}`}>
                            {payment.payment_status}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-500 max-w-xs truncate">
                            {payment.notes || '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => onEdit(payment)}
                              className="text-indigo-600 hover:text-indigo-900"
                              title="Edit"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(payment.id)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentHistory;